import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  CasesValue,
  TAiCaseExamplesResponse,
  TAiScoringTip,
} from "../../../services/api/instances/casesApi/types";
import type { CasesState, TCaseEntityMode, TCaseMode } from "./types";
import { ChatCompletionsMessageObject } from "../../../services/api/instances/completionsApi/types";
import {
  generateCaseSolutionPromptReducerBuilder,
  getAiExamplesReducerBuilder,
  getAiScoringReducerBuilder,
  getAiTipReducerBuilder,
} from "./reducers";

const initialState: CasesState = {
  caseValue: null,
  entityMode: "case",
  dirtyFields: {},
  aiExamples: {},
  aiTip: null,
  messages: [],
  caseByLlm: {},
  caseMode: "create",
  isCasesLoading: false,
  isMessageLoading: false,
  isTipLoading: {},
  isExamplesLoading: false,
  isSolutionLoading: false,
};

const casesSlice = createSlice({
  name: "cases",
  initialState,
  reducers: {
    setCaseValue: (state, action: PayloadAction<CasesValue>) => {
      state.caseValue = action.payload;
    },
    setCaseEntityMode: (state, action: PayloadAction<TCaseEntityMode>) => {
      state.entityMode = action.payload;
    },
    setAiExamples: (
      state,
      action: PayloadAction<{
        id: string;
        value: TAiCaseExamplesResponse | null;
      }>,
    ) => {
      const { id, value } = action.payload;
      state.aiExamples[id] = value;
    },
    setCaseAiTip: (state, action: PayloadAction<TAiScoringTip>) => {
      state.aiTip = action.payload;
    },
    setCaseByLlm: (
      state,
      action: PayloadAction<{
        id: string;
        caseObj: CasesValue["case_object"] | null;
      }>,
    ) => {
      const { id, caseObj } = action.payload;
      state.caseByLlm[id] = caseObj;
    },
    updateCaseField: (
      state: CasesState,
      action: PayloadAction<{ path: string; value: unknown }>,
    ) => {
      const { path, value } = action.payload;
      const [blockKey, fieldKey] = path.split(".");

      if (!state.caseValue) return;

      const rootKey =
        state.entityMode === "agent" ? "agent_harness" : "case_object";

      const block =
        state.caseValue[rootKey as keyof CasesValue][
          blockKey as keyof CasesValue[typeof rootKey]
        ];

      if (block && fieldKey) {
        (block as Record<string, unknown>)[fieldKey] = value;
      }
    },
    markFieldDirty: (state, action: PayloadAction<string>) => {
      state.dirtyFields[action.payload] = true;
    },
    resetDirtyFields: (state) => {
      state.dirtyFields = {};
    },
    addUserMessage: {
      reducer(
        state: CasesState,
        action: PayloadAction<ChatCompletionsMessageObject>,
      ) {
        state.messages.push(action.payload);
      },
      prepare(content: string) {
        return {
          payload: {
            role: "user",
            content,
          },
        };
      },
    },
    setCaseMode: (state, action: PayloadAction<TCaseMode>) => {
      state.caseMode = action.payload;
    },
    clearAiTip: (state) => {
      state.aiTip = null;
    },
    clearAiExamples: (state, action: PayloadAction<string>) => {
      delete state.aiExamples[action.payload];
    },
    clearMessages: (state) => {
      state.messages = [];
    },
  },
  extraReducers: (builder) => {
    getAiScoringReducerBuilder(builder);
    getAiExamplesReducerBuilder(builder);
    getAiTipReducerBuilder(builder);
    generateCaseSolutionPromptReducerBuilder(builder);
  },
});

export const casesReducer = casesSlice.reducer;
export const {
  setCaseValue,
  updateCaseField,
  setAiExamples,
  setCaseAiTip,
  setCaseMode,
  markFieldDirty,
  resetDirtyFields,
  addUserMessage,
  setCaseByLlm,
  clearAiTip,
  clearAiExamples,
  setCaseEntityMode,
  clearMessages,
} = casesSlice.actions;
